import * as React from 'react';
import { Phone, PhoneOff, PhoneIncoming, Pause, Play, Mic, MicOff, ArrowRightLeft, Users, Grid3x3, Wifi, WifiOff } from 'lucide-react';
import { usePhone } from '@/store/phone';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fmtDuration } from '@/lib/utils';
import { api, ApiError, errorText } from '@/lib/api';
import { toast } from '@/store/toast';
import { t } from '@/i18n';
import { CallAlertsButton } from '@/lib/callAlerts';

function useTick(active: boolean) {
  const [, setN] = React.useState(0);
  React.useEffect(() => { if (!active) return; const id = setInterval(() => setN((n) => n + 1), 1000); return () => clearInterval(id); }, [active]);
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

export function Softphone() {
  const p = usePhone();
  const [pad, setPad] = React.useState(false);
  const [number, setNumber] = React.useState('');
  const [xfer, setXfer] = React.useState('');
  const [consultTarget, setConsultTarget] = React.useState('');
  // conference: the 3-way is on (customer, this agent and the target all hear each other)
  const [consult, setConsult] = React.useState<{ target: string; conference: boolean } | null>(null);
  useTick(p.call === 'active');
  const run = (f: () => Promise<void>) => () => { f().catch((e) => toast.error(String(e?.message ?? e))); };

  React.useEffect(() => { if (p.call !== 'active') setConsult(null); }, [p.call]);

  async function startConsult() {
    try {
      await api('/agent/transfer/consult/start', { body: { target: consultTarget } });
      setConsult({ target: consultTarget, conference: false });
      setConsultTarget('');
    } catch (e) { toast.error(errorText(e)); }
  }
  // NO_CONSULT: the consult already ended (the target declined, hung up or left the 3-way, and the
  // engine put the customer back with this agent), so there is nothing left to complete or cancel
  function consultError(e: unknown) {
    if (e instanceof ApiError && e.code === 'NO_CONSULT') { setConsult(null); toast.ok(t('The consult had ended; you are back with the customer')); }
    else toast.error(errorText(e));
  }
  async function completeConsult() {
    if (!consult) return;
    try { await api('/agent/transfer/consult/complete', { body: {} }); setConsult(null); }
    catch (e) { consultError(e); }
  }
  async function conferenceConsult() {
    if (!consult) return;
    try { await api('/agent/transfer/consult/conference', { body: {} }); setConsult({ ...consult, conference: true }); }
    catch (e) { consultError(e); }
  }
  async function cancelConsult() {
    if (!consult) return;
    try { await api('/agent/transfer/consult/cancel', { body: {} }); setConsult(null); }
    catch (e) { consultError(e); }
  }

  const regBadge = p.reg === 'registered'
    ? <Badge variant="success" data-testid="sip-status"><Wifi className="mr-1 h-3 w-3" />{t('registered')}</Badge>
    : <Badge variant={p.reg === 'failed' ? 'destructive' : 'secondary'} data-testid="sip-status"><WifiOff className="mr-1 h-3 w-3" />{t(p.reg)}</Badge>;

  return (
    <Card className="w-full" data-testid="softphone">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{t('Softphone')}</CardTitle>{regBadge}
      </CardHeader>
      <CardContent className="grid gap-3">
        {p.regError && <div className="text-xs text-destructive">{p.regError}</div>}
        <CallAlertsButton />
        {p.call === 'idle' && (
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (number) run(() => p.dial(number))(); }}>
            <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder={t('Extension or number')} aria-label={t('Dial')} disabled={p.reg !== 'registered'} />
            <Button type="submit" size="icon" variant="success" aria-label={t('Call')} disabled={p.reg !== 'registered' || !number}><Phone /></Button>
          </form>
        )}
        {p.call === 'incoming' && (
          <div className="grid gap-2 rounded-lg border border-blue-500/40 bg-blue-500/10 p-3" data-testid="incoming">
            <div className="flex items-center gap-2 font-medium"><PhoneIncoming className="h-4 w-4 animate-pulse" /> {t('Incoming call')}</div>
            <div className="text-sm">{p.remote}</div>
            <div className="flex gap-2">
              <Button variant="success" className="flex-1" onClick={run(p.answer)}><Phone /> {t('Answer')}</Button>
              <Button variant="destructive" className="flex-1" onClick={run(p.reject)}><PhoneOff /> {t('Reject')}</Button>
            </div>
          </div>
        )}
        {p.call === 'outgoing' && (
          <div className="grid gap-2 rounded-lg border p-3">
            <div className="text-sm">{t('Calling {{remote}}…', { remote: p.remote })}</div>
            <Button variant="destructive" onClick={run(p.hangup)}><PhoneOff /> {t('Cancel')}</Button>
          </div>
        )}
        {p.call === 'active' && (
          <div className="grid gap-2 rounded-lg border border-success/40 bg-success/10 p-3" data-testid="in-call">
            <div className="flex items-center justify-between">
              <span className="font-medium">{p.remote}</span>
              <span className="tabular-nums text-sm" data-testid="call-timer">{p.held ? t('on hold') : fmtDuration(p.since ? (Date.now() - p.since) / 1000 : 0)}</span>
            </div>
            <div className="grid grid-cols-4 gap-1">
              {p.held
                ? <Button size="sm" variant="outline" onClick={run(p.resume)} aria-label={t('Resume')}><Play /></Button>
                : <Button size="sm" variant="outline" onClick={run(p.hold)} aria-label={t('Hold')}><Pause /></Button>}
              <Button size="sm" variant={p.muted ? 'warning' : 'outline'} onClick={p.toggleMute} aria-label={p.muted ? t('Unmute') : t('Mute')}>{p.muted ? <MicOff /> : <Mic />}</Button>
              <Button size="sm" variant={pad ? 'secondary' : 'outline'} onClick={() => setPad((x) => !x)} aria-label={t('Keypad')}><Grid3x3 /></Button>
              <Button size="sm" variant="destructive" onClick={run(p.hangup)} aria-label={t('Hang up')}><PhoneOff /></Button>
            </div>
            {pad && <div className="grid grid-cols-3 gap-1">{KEYS.map((k) => <Button key={k} size="sm" variant="outline" onClick={run(() => p.dtmf(k))} aria-label={`DTMF ${k}`}>{k}</Button>)}</div>}
            {!consult && (
              <form className="flex gap-1" onSubmit={(e) => { e.preventDefault(); if (xfer) run(() => p.transfer(xfer))(); }}>
                <Input className="h-8" value={xfer} onChange={(e) => setXfer(e.target.value)} placeholder={t('Transfer to…')} aria-label={t('Transfer target')} />
                <Button size="sm" variant="outline" type="submit" disabled={!xfer} aria-label={t('Transfer')}><ArrowRightLeft /></Button>
              </form>
            )}
            {!consult && (
              <form className="flex gap-1" onSubmit={(e) => { e.preventDefault(); if (consultTarget) startConsult(); }}>
                <Input className="h-8" value={consultTarget} onChange={(e) => setConsultTarget(e.target.value)} placeholder={t('Consult with…')} aria-label={t('Consult target')} />
                <Button size="sm" variant="outline" type="submit" disabled={!consultTarget} aria-label={t('Consult')}><Users /></Button>
              </form>
            )}
            {consult && !consult.conference && (
              <div className="grid gap-2 rounded-lg border border-blue-500/40 bg-blue-500/10 p-3" data-testid="consulting">
                <div className="text-sm">{t('Consulting with {{target}}… the customer is on hold.', { target: consult.target })}</div>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="success" onClick={completeConsult}>{t('Complete transfer')}</Button>
                  <Button size="sm" variant="outline" onClick={cancelConsult}>{t('Back to customer')}</Button>
                  <Button size="sm" variant="outline" className="col-span-2" onClick={conferenceConsult} data-testid="consult-conference"><Users /> {t('Conference (3-way)')}</Button>
                </div>
              </div>
            )}
            {consult?.conference && (
              <div className="grid gap-2 rounded-lg border border-violet-500/40 bg-violet-500/10 p-3" data-testid="conference">
                <div className="text-sm">{t('3-way with the customer and {{target}}: everyone hears everyone.', { target: consult.target })}</div>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="success" onClick={completeConsult} title={t('You leave; the customer and the target stay connected')}>{t('Leave call')}</Button>
                  <Button size="sm" variant="outline" onClick={cancelConsult} title={t('Drop {{target}}; you stay with the customer', { target: consult.target })}>{t('Drop {{target}}', { target: consult.target })}</Button>
                </div>
              </div>
            )}
          </div>
        )}
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={p.autoAnswer} onChange={(e) => p.setAutoAnswer(e.target.checked)} /> {t('Answer every call automatically')}
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground" title={t('Logs SIP.js debug output and every SIP message in the browser console (F12)')}>
          <input type="checkbox" checked={p.sipDebug} onChange={(e) => p.setSipDebug(e.target.checked)} data-testid="sip-debug" /> {t('SIP debug log (browser console)')}
        </label>
      </CardContent>
    </Card>
  );
}
